import React, { PropsWithChildren, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Auth, authorizeActions, authorizeSelectors } from "./store/authorize";
import { useDispatch, useSelector } from "./store/store";
import { useStorage } from "./hooks/storage";
import axios from "axios";
import { StorageType } from "./services/storage.default";
import { refreshToken } from "./repository/auth.repository";
import { useSignal } from "./hooks/signal";

// Http - Guard control http and intercept all requests

export const http = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
  timeout: 30000, // 30s — heavy loads (questionnaires/KPIs) exceeded the old 10s on UAT
  headers: {
    "Content-Type": "application/json",
  },
});

export const publicHttp = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
  timeout: 30000, // 30s — heavy loads (questionnaires/KPIs) exceeded the old 10s on UAT
  headers: {
    "Content-Type": "application/json",
  },
});

// Guard Context - Storage for guard state

interface Context {
  readonly loading?: boolean;
  readonly authorized?: boolean;
  readonly nointernet?: boolean;
  readonly keepLoggedIn?: boolean;
  readonly signIn: (user: Auth, keepLoggedIn: boolean) => void;
  readonly signOut: () => void;
  readonly connected: boolean;
  readonly on: (name: string, fn: (...args: any[]) => void) => void;
  readonly off: (name: string, fn: (...args: any[]) => void) => void;
  readonly send: (name: string, ...args: any[]) => void;
}

const GuardContext = React.createContext<Context>(null!);

// Guard hook - Get guard state anywhere in your app

export function useGuard() {
  return React.useContext(GuardContext);
}

// Guard Provider - Provider to keep state of guard for all app

export interface GuardProviderProps {}

export function GuardProvider(props: PropsWithChildren<GuardProviderProps>) {
  const dispatch = useDispatch();
  const localStorage = useStorage("local");
  const storage = useStorage();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const [nointernet, setNointernet] = useState(false);
  const auth = useSelector(authorizeSelectors.auth);
  const [signal] = useSignal(`${process.env.REACT_APP_API_HOST!}/apphub`);

  useEffect(() => {
    const storageType = localStorage.get<StorageType>(
      "AUTH_STORAGE_TYPE",
      "session",
    )!;
    storage.on(storageType);
    const auth = storage.get<Auth | null>("AUTH");
    if (auth != null) {
      dispatch(authorizeActions.signIn(auth));
    } else {
      dispatch(authorizeActions.signOut());
    }
  }, []);

  useEffect(() => {
    setLoading(false);
    setAuthorized(auth?.token != null);
  }, [auth]);

  useEffect(() => {
    const requestInterceptor = http.interceptors.request.use(
      (request) => {
        // Read the LIVE token from storage (not the stale React closure) so a request retried after a
        // token refresh carries the fresh token — fixes the silent logout that broke "keep me logged in".
        const token = storage.get<Auth | null>("AUTH")?.token ?? auth?.token;
        if (token) {
          request.headers.Authorization = `Bearer ${token}`;
        }
        return request;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
    const responseInterceptor = http.interceptors.response.use(
      (response) => {
        setNointernet(false);

        return response;
      },
      async (error) => {
        const request = error.config;
        setNointernet(error.request.status === 0);
        // Only attempt a refresh once per request (prevents a refresh loop on a still-401 retry).
        if (error.response?.status === 401 && request && !request._retry) {
          request._retry = true;
          try {
            // Use the LIVE auth from storage for the refresh — the closure can be stale.
            const current = storage.get<Auth | null>("AUTH") ?? auth;
            const newAuth = await refreshToken({
              token: current?.token!,
              refreshToken: current?.refreshToken!,
            })
              .then((result) => {
                return result;
              })
              .catch(() => {
                return null;
              });
            if (newAuth) {
              storage.set("AUTH", newAuth);
              dispatch(authorizeActions.signIn(newAuth));
              setAuthorized(newAuth.token != null);
              request.headers.Authorization = `Bearer ${newAuth.token}`;
              return http(request);
            } else {
              dispatch(authorizeActions.signOut());
              setLoading(false);
              setAuthorized(false);
            }
          } catch {
            dispatch(authorizeActions.signOut());
            setLoading(false);
            setAuthorized(false);
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      http.interceptors.request.eject(requestInterceptor);
      http.interceptors.response.eject(responseInterceptor);
    };
  }, [auth]);

  useEffect(() => {
    if (authorized) {
      signal.start(auth?.token!);
    } else {
      signal.close();
    }

    return () => {
      signal.close();
    };
  }, [auth, authorized]);

  function signIn(auth: Auth, keepLoggedIn: boolean) {
    storage.on(keepLoggedIn ? "local" : "session");
    storage.set("AUTH", auth);
    localStorage.set("AUTH_STORAGE_TYPE", keepLoggedIn ? "local" : "session");
    dispatch(authorizeActions.signIn(auth));
    setAuthorized(auth.token != null);
    setKeepLoggedIn(keepLoggedIn);
  }

  function signOut() {
    storage.clear(true);
    dispatch(authorizeActions.signOut());
    setAuthorized(false);
  }

  const value = {
    // Auth
    authorized,
    nointernet,
    keepLoggedIn,
    loading,
    signIn,
    signOut,

    // Hub
    connected: signal.connected,
    on: (name: string, fn: (...args: any[]) => void) => signal.on(name, fn),
    off: (name: string, fn: (...args: any[]) => void) => signal.off(name, fn),
    send: (name: string, ...args: any[]) => signal.send(name, ...args),
  };

  return (
    <GuardContext.Provider value={value}>
      {props.children}
    </GuardContext.Provider>
  );
}

// Guard - Wrap root page which must be public or protected

interface GuardProps {
  readonly public?: boolean;
  readonly redirectTo?: string;
}

function Guard(props: PropsWithChildren<GuardProps>) {
  const { authorized, loading } = useGuard();

  if ((props.public && authorized) || (!props.public && !authorized)) {
    return <>{!loading && <Navigate to={props.redirectTo ?? "/"} replace />}</>;
  }

  return <>{props.children}</>;
}

export default Guard;
